<script setup lang="ts">
import { ref } from 'vue';
import type { ICampaignMedia } from './campaign.types';

const props = defineProps<{
  title: string;
  media: ICampaignMedia | null;
  uploading: boolean;
  pendingDeleteId: string | null;
  compact?: boolean;
}>();

const emit = defineEmits<{
  upload: [file: File];
  replace: [media: ICampaignMedia];
  remove: [media: ICampaignMedia];
}>();

const inputRef = ref<HTMLInputElement | null>(null);

function onFileChange(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0] ?? null;
  (e.target as HTMLInputElement).value = '';
  if (file) emit('upload', file);
}

function triggerReplace() {
  if (props.media) emit('replace', props.media);
}

defineExpose({ openPicker: () => inputRef.value?.click() });
</script>

<template>
  <section class="csm">
    <h3 class="csm__title">{{ title }}</h3>
    <div class="csm__row">
      <div
        v-if="media"
        class="csm__preview"
        :class="{ 'csm__preview--sm': compact }"
      >
        <img :src="media.imageUrl" :alt="title" />
      </div>
      <div
        v-else
        class="csm__placeholder"
        :class="{ 'csm__placeholder--sm': compact }"
      >
        <i class="pi pi-image" />
        <span>Aucune image</span>
      </div>

      <div class="csm__actions">
        <button
          v-if="!media"
          class="csm__btn csm__btn--primary"
          :disabled="uploading"
          @click="inputRef?.click()"
        >
          <i v-if="uploading" class="pi pi-spin pi-spinner" />
          <i v-else class="pi pi-upload" />
          Ajouter
        </button>
        <template v-else>
          <button
            class="csm__btn csm__btn--ghost"
            :disabled="uploading || pendingDeleteId === media.id"
            @click="triggerReplace"
          >
            <i
              v-if="uploading || pendingDeleteId === media.id"
              class="pi pi-spin pi-spinner"
            />
            <i v-else class="pi pi-refresh" />
            Remplacer
          </button>
          <button
            class="csm__btn csm__btn--danger"
            :disabled="pendingDeleteId === media.id"
            @click="emit('remove', media)"
          >
            <i class="pi pi-trash" /> Supprimer
          </button>
        </template>
        <input
          ref="inputRef"
          type="file"
          accept="image/*"
          hidden
          @change="onFileChange"
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
.csm {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  background: var(--nxt-card-bg);
}
.csm__title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.csm__row {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
}
.csm__preview {
  width: 12rem;
  height: 6rem;
  border-radius: 0.625rem;
  overflow: hidden;
  border: 1px solid var(--nxt-outline-variant);
  flex-shrink: 0;
}
.csm__preview--sm {
  width: 6rem;
  height: 6rem;
}
.csm__preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.csm__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  width: 12rem;
  height: 6rem;
  border-radius: 0.625rem;
  border: 1px dashed var(--nxt-outline-variant);
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
  flex-shrink: 0;
}
.csm__placeholder--sm {
  width: 6rem;
  height: 6rem;
}
.csm__placeholder i {
  font-size: 1.25rem;
  opacity: 0.5;
}
.csm__actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.csm__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.375rem;
  padding: 0 1rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
}
.csm__btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
.csm__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.csm__btn--danger {
  background: rgba(239, 68, 68, 0.1);
  color: var(--nxt-error);
}
.csm__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
